import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserDashboard from '../dashboards/UserDashboard';
import './IntegrationEntry.css';

/**
 * Ticket creation entry for decrypted integration data.
 * URL: /ticket-create
 * Reads the payload from sessionStorage and renders the existing UserForm
 * inside UserDashboard. The dashboard is hidden until the form is submitted.
 */
const TicketCreateEntry = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [integrationUser, setIntegrationUser] = useState(null);
  const [restoredFromSession, setRestoredFromSession] = useState(false);

  useEffect(() => {
    // A fresh integration payload in sessionStorage means a new integration link
    // was opened. Clear ALL stale session data from any previous login/integration
    // to prevent cross-organization data leakage.
    const rawPayload = sessionStorage.getItem('integrationPayload');
    if (rawPayload) {
      localStorage.removeItem('integrationSession');
      localStorage.removeItem('customerData');
      localStorage.removeItem('customerToken');
      localStorage.removeItem('access_token');
      localStorage.removeItem('customer_id');
      localStorage.removeItem('customer_name');
      localStorage.removeItem('customer_email');
      localStorage.removeItem('customer_role');
      localStorage.removeItem('autoLoginContext');
      localStorage.removeItem('userData');
      localStorage.removeItem('userToken');
      localStorage.removeItem('tickUser');
      try {
        const payload = JSON.parse(rawPayload);
        // name/email may be empty when the user comes from an external
        // application's login page (anonymous flow). They will fill these
        // manually on the ticket form. product/module are still mandatory.
        if (!payload.product || !payload.module) {
          throw new Error('Invalid ticket data');
        }
        const user = {
          id: null,
          email: payload.email || '',
          name: payload.name || '',
          role: 'user',
          _supportMeta: { isNewUser: true, hasTickets: false, isIntegration: true }
        };
        setIntegrationUser(user);
        setStatus('success');
        return;
      } catch (err) {
        setError(err.message || 'Invalid ticket creation data');
        setStatus('error');
        return;
      }
    }

    // No fresh payload — check for an existing session from a previous submission
    // so the dashboard survives page refresh.
    const sessionRaw = localStorage.getItem('integrationSession');
    if (sessionRaw) {
      try {
        const session = JSON.parse(sessionRaw);
        const user = {
          id: session.userId,
          email: session.email,
          name: session.name,
          tenant_id: session.tenantId,
          role: 'user',
          _supportMeta: { isNewUser: false, hasTickets: true, isIntegration: true }
        };
        setIntegrationUser(user);
        setRestoredFromSession(true);
        setStatus('success');
        return;
      } catch (err) {
        localStorage.removeItem('integrationSession');
      }
    }

    setError('Ticket creation data is missing. Please use the link provided by the external application.');
    setStatus('error');
  }, []);

  if (status === 'loading') {
    return (
      <div className="integration-entry">
        <div className="integration-entry-content">
          <div className="integration-entry-spinner" />
          <h2>Loading ticket form...</h2>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="integration-entry">
        <div className="integration-entry-content integration-entry-error">
          <h2>Ticket Creation Error</h2>
          <p className="integration-entry-error-msg">{error}</p>
          <button onClick={() => navigate('/')} className="integration-entry-btn">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <UserDashboard
      user={integrationUser}
      isFirstTimeSupportUser={true}
      initialShowForm={!restoredFromSession}
    />
  );
};

export default TicketCreateEntry;
